import { getPagination, getTableFilter, handleUrlChange } from '@TerraCycleUS/loop-antd-filter'
import { ClockCircleOutlined } from '@ant-design/icons'
import { Badge, Button, Col, notification, Popconfirm, Row, Table, Timeline, Tooltip } from 'antd'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Can, Icon, IconContainer } from '../../../../components'
import { handleBackendError } from '../../../../utils'
import { cancelOrderRequest, deleteOrderRequest, getStoreOrders } from '../../api'
import NewOrderModal from './NewOrderModal'

const lifecycleSteps = [
  { label: 'New', status: 'new', timestampField: 'created_at' },
  { label: 'In progress', status: 'in_progress', timestampField: 'in_progress_at' },
  { label: 'Prepared', status: 'prepared', timestampField: 'prepared_at' },
  { label: 'Ready to ship', status: 'ready_to_ship', timestampField: 'ready_to_ship_at' },
  { label: 'With carrier', status: 'with_carrier', timestampField: 'with_carrier_at' },
  { label: 'Delivered', status: 'delivered', timestampField: 'delivered_at' },
  { label: 'Cancelled', status: 'cancelled', timestampField: 'cancelled_at' },
]

const statusLabels = Object.fromEntries(lifecycleSteps.map(({ status, label }) => [status, label]))

const statusFilters = lifecycleSteps.map(({ label, status }) => ({ text: label, value: status }))

const timelineSteps = lifecycleSteps.filter(({ status }) => status !== 'cancelled')

const delayIndicatorColors = {
  at_risk: 'crimson',
  delayed: 'orange',
  on_time: 'green',
}

const businessHoursElapsed = (from, to) => {
  const hours = Math.min(dayjs(to).diff(dayjs(from), 'hour'), 49)
  return Array.from({ length: hours }, (_, i) => dayjs(from).add(i, 'hour'))
    .filter((h) => h.day() !== 0 && h.day() !== 6)
    .length
}

const elapsedToDelayStatus = (elapsed) => {
  if (elapsed >= 48) return 'at_risk'
  if (elapsed >= 24) return 'delayed'
  return 'on_time'
}

const getDelayStatus = (order) => {
  if (order.status === 'cancelled' || order.status === 'delivered') return 'on_time'
  if (!order.created_at) return 'on_time'
  return elapsedToDelayStatus(businessHoursElapsed(order.created_at, dayjs()))
}

const Orders = ({ selectedStore }) => {
  const [orders, setOrders] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [metaData, setMetaData] = useState()
  const [isOrderModalVisible, setOrderModalVisibility] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const hasAppliedOwnParams = useRef(false)

  const fetchOrders = (paramsString) => {
    if (!selectedStore) return

    setIsLoading(true)

    const params = new URLSearchParams(paramsString || '')
    params.set('store_id_eq', selectedStore.id)

    getStoreOrders(params)
      .then(({ data }) => {
        setMetaData(data.meta)
        setOrders(data.data)
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    const previousParams = searchParams.toString()
    const defaultParams = { items_per_page: 25, page: 1, sort_by: 'created_at DESC' }

    setSearchParams(defaultParams)
    fetchOrders(new URLSearchParams(defaultParams).toString())

    return () => setSearchParams(new URLSearchParams(previousParams))
  }, [])

  useEffect(() => {
    if (!hasAppliedOwnParams.current) {
      hasAppliedOwnParams.current = true
      return
    }

    if (searchParams.get('page') === 'null') return

    fetchOrders(searchParams.toString())
  }, [searchParams, selectedStore?.id])

  const handleCancel = (id) => {
    cancelOrderRequest(id)
      .then(() => {
        notification.success({ description: 'Order request cancelled', title: 'Success' })

        fetchOrders(searchParams.toString())
      })
      .catch((error) => {
        notification.error({
          description: handleBackendError(error, 'Order request cancellation failed!'),
          title: 'Error',
        })
      })
  }

  const handleDelete = (id) => {
    deleteOrderRequest(id)
      .then(() => {
        notification.success({ description: 'Order request deleted', title: 'Success' })

        fetchOrders(searchParams.toString())
      })
      .catch((error) => {
        notification.error({
          description: handleBackendError(error, 'Order request deletion failed!'),
          title: 'Error',
        })
      })
  }

  const tableColumns = [
    {
      dataIndex: 'order_number',
      key: 'order_number',
      render: (id) => id || '-',
      title: 'Order number',
      ...getTableFilter({ name: 'order_number_cont' }),
    },
    {
      dataIndex: 'tracking_number',
      render: (trackingNumber) => trackingNumber || '-',
      title: 'Tracking Number',
      ...getTableFilter({ name: 'tracking_number_cont' }),
    },
    {
      key: 'totes',
      render: (_, record) => record.tote_quantity,
      title: 'Quantity',
    },
    {
      dataIndex: 'created_at',
      render: (date) => dayjs(date).format('DD.MM.YYYY HH:mm'),
      sorter: true,
      title: 'Created at',
      ...getTableFilter({ name: 'created_at', type: 'date' }),
    },
    {
      dataIndex: 'updated_at',
      render: (date) => dayjs(date).format('DD.MM.YYYY HH:mm'),
      sorter: true,
      title: 'Updated at',
      ...getTableFilter({ name: 'updated_at', type: 'date' }),
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => {
        const delayStatus = getDelayStatus(record)
        if (delayStatus === 'on_time') return statusLabels[record.status]
        return <Badge color={delayIndicatorColors[delayStatus]} text={statusLabels[record.status]} />
      },
      sorter: true,
      title: 'Status',
      ...getTableFilter({
        filterValues: statusFilters,
        isMultiple: true,
        name: 'status_in[]',
        type: 'checkbox',
      }),
    },
    {
      key: 'actions',
      render: (_, record) => (
        <IconContainer>
          {record.deletable && (
            <Can
              requiredPermission="order_request.delete.order_request_id"
              yes={() => (
                <Tooltip title="Delete order request">
                  <Popconfirm
                    cancelText="No"
                    okText="Yes"
                    onConfirm={() => handleDelete(record.id)}
                    title="Are you sure you want to delete this order request?"
                  >
                    <Icon data-testid="deleteOrderIcon" name="deleteOrderSelenium" type="delete" />
                  </Popconfirm>
                </Tooltip>
              )}
            />
          )}

          {record.cancellable && (
            <Can
              requiredPermission="order_request.put.order_request_id_cancel"
              yes={() => (
                <Tooltip title="Cancel order request">
                  <Popconfirm
                    cancelText="No"
                    okText="Yes"
                    onConfirm={() => handleCancel(record.id)}
                    title="Are you sure you want to cancel this order request?"
                  >
                    <Icon
                      data-testid="cancelOrderIcon"
                      name="cancelOrderSelenium"
                      style={{ color: 'red' }}
                      type="closeCircle"
                    />
                  </Popconfirm>
                </Tooltip>
              )}
            />
          )}
        </IconContainer>
      ),
      title: 'Action',
    },
  ]

  const getTimelineItems = (order) => {
    const currentStatus = order.status
    const mainSteps = timelineSteps.map(({ label, status, timestampField }, index) => {
      const timestamp = order[timestampField]
      const isCompleted = !!timestamp
      const isCurrent = status === currentStatus
      const prevTimestamp = index > 0 ? order[timelineSteps[index - 1].timestampField] : null

      const from = index === 0 ? (isCurrent ? order.created_at : null) : prevTimestamp
      const stepDelayStatus = !from || status === 'delivered'
        ? 'on_time'
        : elapsedToDelayStatus(businessHoursElapsed(from, isCurrent ? dayjs() : timestamp))

      return {
        color: isCompleted ? delayIndicatorColors[stepDelayStatus] : 'gray',
        content: isCompleted
          ? label
          : <span style={{ color: '#bfbfbf' }}>{label}</span>,
        icon: isCurrent && stepDelayStatus !== 'on_time'
          ? <ClockCircleOutlined style={{ color: delayIndicatorColors[stepDelayStatus] }} />
          : undefined,
        key: status,
        title: isCompleted
          ? (
            <>
              <div>{dayjs(timestamp).format('DD.MM.YYYY HH:mm')}</div>
              {status === 'new' && <div style={{ color: '#8c8c8c' }}>{order.requester_email}</div>}
            </>
          )
          : null,
      }
    })

    if (order.cancelled_at) {
      mainSteps.push({
        color: 'red',
        content: statusLabels['cancelled'],
        key: 'cancelled',
        title: <div>{dayjs(order.cancelled_at).format('DD.MM.YYYY HH:mm')}</div>,
      })
    }

    return mainSteps
  }

  return (
    <Row style={{ margin: 15 }}>
      <Col span={24}>
        <Row justify="end" style={{ marginBottom: 16 }}>
          {selectedStore && (
            <Can
              requiredPermission="order_request.post.create"
              yes={() => (
                <Button
                  data-testid="newAdminOrderBtn"
                  name="newAdminOrderSelenium"
                  onClick={() => setOrderModalVisibility(true)}
                  type="primary"
                >
                  New Admin Order
                </Button>
              )}
            />
          )}
        </Row>

        <Table
          className="storeOrdersTableSelenium"
          columns={tableColumns}
          dataSource={orders}
          expandable={{
            expandedRowRender: (record) => (
              <Timeline items={getTimelineItems(record)} mode="start" />
            ),
          }}
          loading={isLoading}
          onChange={(pagination, filters, sorter) => handleUrlChange(pagination, filters, sorter, setSearchParams)}
          pagination={getPagination(metaData?.page, metaData?.items_per_page, metaData?.total_items)}
          rowClassName={(record) => (record.cancelled_at ? 'cancelled-order-row' : '')}
          rowKey="id"
        />

        {isOrderModalVisible && (
          <NewOrderModal
            hideModal={() => setOrderModalVisibility(false)}
            onSuccess={() => fetchOrders(Object.fromEntries(searchParams))}
            selectedStore={selectedStore}
          />
        )}
      </Col>
    </Row>
  )
}

Orders.propTypes = {
  selectedStore: PropTypes.object,
}

export default Orders
